import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Send,
  Image as ImageIcon,
  Smile,
  Calendar,
  MessageCircle,
  ArrowLeft,
} from 'lucide-react';
import {
  getDmConversations,
  getDmMessages,
  sendDm,
  type DmConversation,
  type DmMessage,
} from '@/api/social';

interface MessagingHubProps {
  userType: 'owner' | 'professional';
  userId: string;
  initialChatWith?: { id: string; name: string; avatar?: string | null } | null;
}

export function MessagingHub({
  userType,
  userId: _userId,
  initialChatWith,
}: MessagingHubProps) {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const [conversations, setConversations] = useState<DmConversation[]>([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(
    null
  );
  const [selectedPartnerName, setSelectedPartnerName] = useState<string | null>(
    null
  );
  const [selectedPartnerAvatar, setSelectedPartnerAvatar] = useState<
    string | null
  >(null);
  const [messages, setMessages] = useState<DmMessage[]>([]);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load conversations and poll every 5s
  const loadConversations = useCallback(async () => {
    try {
      const convs = await getDmConversations();
      setConversations(convs);
    } catch {
      // silently ignore
    }
  }, []);

  useEffect(() => {
    void loadConversations();
    const interval = setInterval(() => {
      void loadConversations();
    }, 5000);
    return () => clearInterval(interval);
  }, [loadConversations]);

  // Load messages for selected partner and poll every 3s
  const loadMessages = useCallback(async (partnerId: string) => {
    try {
      const { messages: msgs } = await getDmMessages(partnerId, { limit: 50 });
      setMessages(msgs);
      // Update conversation unread count locally
      setConversations((prev) =>
        prev.map((c) =>
          c.partnerId === partnerId ? { ...c, unreadCount: 0 } : c
        )
      );
    } catch {
      // silently ignore
    }
  }, []);

  useEffect(() => {
    if (!selectedPartnerId) return;
    void loadMessages(selectedPartnerId);
    const interval = setInterval(() => {
      void loadMessages(selectedPartnerId);
    }, 3000);
    return () => clearInterval(interval);
  }, [selectedPartnerId, loadMessages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Open chat from initialChatWith prop
  useEffect(() => {
    if (!initialChatWith) return;
    setSelectedPartnerId(initialChatWith.id);
    setSelectedPartnerName(initialChatWith.name);
    setSelectedPartnerAvatar(initialChatWith.avatar ?? null);
    setMessages([]);
  }, [initialChatWith?.id]);

  const openConversation = (conv: DmConversation) => {
    setSelectedPartnerId(conv.partnerId);
    setSelectedPartnerName(conv.partnerName);
    setSelectedPartnerAvatar(conv.partnerAvatar);
    setMessages([]);
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedPartnerId || sending) return;
    const text = messageText.trim();
    setMessageText('');
    setSending(true);
    try {
      const msg = await sendDm(selectedPartnerId, text);
      setMessages((prev) => [...prev, msg]);
      // Refresh conversations to update last message
      void loadConversations();
    } catch {
      // Restore message text on failure
      setMessageText(text);
    } finally {
      setSending(false);
    }
  };

  const filteredConversations = conversations.filter(
    (c) =>
      !searchQuery ||
      (c.partnerName ?? '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeConv = selectedPartnerId
    ? conversations.find((c) => c.partnerId === selectedPartnerId)
    : null;

  const displayName = activeConv?.partnerName ?? selectedPartnerName ?? '';
  const displayAvatar = activeConv?.partnerAvatar ?? selectedPartnerAvatar;

  const showList = !isMobile || !selectedPartnerId;
  const showThread = !isMobile || !!selectedPartnerId;

  return (
    <div className="py-4 sm:py-6 px-3 sm:px-4 min-w-0">
      <div className="max-w-6xl mx-auto min-w-0">
        <div
          className="bg-white dark:bg-[#16151D] rounded-2xl overflow-hidden border border-[#E2DFEA] dark:border-white/8 min-h-[70vh] sm:min-h-0"
          style={{
            height: isMobile ? 'calc(100vh - 180px)' : 'calc(100vh - 200px)',
          }}
        >
          <div className="flex h-full flex-col md:flex-row min-w-0">
            {/* Conversations List */}
            {showList && (
              <div className="w-full md:w-1/3 flex flex-col border-r border-[#E2DFEA] dark:border-white/8 min-w-0 flex-1 md:flex-initial min-h-0">
                {/* Search */}
                <div className="p-3 sm:p-4 border-b border-[#E2DFEA] dark:border-white/8 min-w-0">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#464452] dark:text-white/40 opacity-40 shrink-0 pointer-events-none" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t('social.messages.searchPlaceholder')}
                      className="w-full min-w-0 pl-10 pr-4 py-2.5 sm:py-2 rounded-xl border border-[#E2DFEA] dark:border-white/20 bg-white dark:bg-[#0F0E16] text-[#464452] dark:text-white/90 placeholder:text-[#464452]/50 dark:placeholder:text-white/50"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    />
                  </div>
                </div>

                {/* Conversation List */}
                <div className="flex-1 overflow-y-auto">
                  {filteredConversations.length === 0 && !selectedPartnerId && (
                    <div className="p-4 sm:p-6 text-center min-w-0">
                      <MessageCircle className="w-12 h-12 mx-auto mb-3 text-[#464452] dark:text-white/20 opacity-40" />
                      <p
                        className="text-sm text-[#464452] dark:text-white/70 break-words"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {t('social.messages.noConversations')}
                      </p>
                      <p
                        className="text-xs text-[#464452] dark:text-white/50 mt-1 break-words px-2"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {t('social.startChatHint')}
                      </p>
                    </div>
                  )}
                  {filteredConversations.map((conv) => (
                    <button
                      key={conv.partnerId}
                      onClick={() => openConversation(conv)}
                      className={`w-full p-4 hover:bg-[#F0EFF5] dark:hover:bg-white/5 transition-colors text-left border-b border-[#E2DFEA] dark:border-white/8 ${
                        selectedPartnerId === conv.partnerId
                          ? 'bg-[#F0EFF5] dark:bg-white/5'
                          : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          {conv.partnerAvatar ? (
                            <img
                              src={conv.partnerAvatar}
                              alt={conv.partnerName ?? ''}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          ) : (
                            <div
                              className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
                              style={{
                                background:
                                  'linear-gradient(135deg, #F0EFF5 0%, #936FEF 100%)',
                              }}
                            >
                              🐾
                            </div>
                          )}
                          {conv.unreadCount > 0 && (
                            <div
                              className="absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white dark:border-[#16151D]"
                              style={{ backgroundColor: '#936FEF' }}
                            />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span
                              className="font-semibold truncate text-[#2D2F7E] dark:text-white"
                              style={{ fontFamily: 'Sora, sans-serif' }}
                            >
                              {conv.partnerName ??
                                t('social.messages.unknownUser')}
                            </span>
                          </div>
                          <div
                            className="text-xs truncate text-[#464452] dark:text-white/50 opacity-60"
                            style={{ fontFamily: 'Inter, sans-serif' }}
                          >
                            {conv.lastMessageFromMe
                              ? `${t('social.you')}: `
                              : ''}
                            {conv.lastMessage}
                          </div>
                        </div>
                        <div
                          className="text-xs flex-shrink-0 text-[#464452] dark:text-white/50 opacity-60"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          {new Date(conv.lastMessageAt).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                      </div>
                    </button>
                  ))}
                  {/* New chat entry when opened via initialChatWith */}
                  {selectedPartnerId && !activeConv && selectedPartnerName && (
                    <div className="border-b border-[#E2DFEA] dark:border-white/8 bg-[#F0EFF5] dark:bg-white/5 px-4 py-3">
                      <div
                        className="flex items-center gap-2 text-sm text-[#936FEF] font-medium"
                        style={{ fontFamily: 'Sora, sans-serif' }}
                      >
                        <MessageCircle className="w-4 h-4" />
                        {t('social.messages.newChatWith', {
                          name: selectedPartnerName,
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Message Thread */}
            {showThread && selectedPartnerId ? (
              <div className="flex-1 flex flex-col min-w-0 min-h-0 w-full">
                {/* Chat Header */}
                <div className="p-3 sm:p-4 flex items-center gap-2 sm:gap-3 border-b border-[#E2DFEA] dark:border-white/8 min-w-0 shrink-0">
                  {isMobile && (
                    <button
                      type="button"
                      onClick={() => setSelectedPartnerId(null)}
                      className="p-2 -ml-2 rounded-lg hover:bg-[#F0EFF5] dark:hover:bg-white/5 text-[#464452] dark:text-white/70 touch-manipulation shrink-0"
                      aria-label={t('social.messages.backToConversations')}
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                  )}
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {displayAvatar ? (
                      <img
                        src={displayAvatar}
                        alt={displayName}
                        className="w-10 h-10 rounded-full object-cover shrink-0"
                      />
                    ) : (
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-xl shrink-0"
                        style={{
                          background:
                            'linear-gradient(135deg, #F0EFF5 0%, #936FEF 100%)',
                        }}
                      >
                        🐾
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div
                        className="font-semibold text-[#2D2F7E] dark:text-white truncate"
                        style={{ fontFamily: 'Sora, sans-serif' }}
                      >
                        {displayName}
                      </div>
                    </div>
                  </div>
                  {userType === 'professional' && activeConv && (
                    <button
                      className="px-4 py-2 rounded-xl border border-[#E2DFEA] dark:border-white/20 text-[#464452] dark:text-white/70 hover:bg-[#F0EFF5] dark:hover:bg-white/5 text-sm flex items-center gap-2 transition-colors shrink-0 touch-manipulation"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      <Calendar className="w-4 h-4" />
                      {t('social.messages.viewBookings')}
                    </button>
                  )}
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-white dark:bg-[#0F0E16]">
                  {messages.length === 0 && (
                    <div
                      className="text-center py-8 text-[#464452] dark:text-white/50 text-sm"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('social.messages.noMessagesYet', {
                        name: displayName,
                      })}
                    </div>
                  )}
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.fromMe ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-xs ${message.fromMe ? 'order-2' : 'order-1'}`}
                      >
                        <div
                          className="px-4 py-2 rounded-2xl"
                          style={
                            message.fromMe
                              ? {
                                  background:
                                    'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                                  color: 'white',
                                  fontFamily: 'Inter, sans-serif',
                                }
                              : {
                                  backgroundColor: '#F0EFF5',
                                  color: '#2D2F7E',
                                  fontFamily: 'Inter, sans-serif',
                                }
                          }
                        >
                          {message.text}
                        </div>
                        <div
                          className="text-xs mt-1 px-2 text-[#464452] dark:text-white/50 opacity-60"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          {new Date(message.createdAt).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <div className="p-4 border-t border-[#E2DFEA] dark:border-white/8">
                  <div className="flex items-center gap-3">
                    <button className="text-[#464452] dark:text-white/70 hover:text-[#936FEF] transition-colors">
                      <ImageIcon className="w-6 h-6" />
                    </button>
                    <button className="text-[#464452] dark:text-white/70 hover:text-[#936FEF] transition-colors">
                      <Smile className="w-6 h-6" />
                    </button>
                    <input
                      type="text"
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          void handleSendMessage();
                        }
                      }}
                      placeholder={t('social.messages.typeMessage')}
                      className="flex-1 px-4 py-2 rounded-full outline-none focus:ring-2 border border-[#E2DFEA] dark:border-white/20 bg-white dark:bg-[#0F0E16] text-[#464452] dark:text-white/90 placeholder:text-[#464452]/50 dark:placeholder:text-white/50"
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        ['--tw-ring-color' as string]: '#936FEF',
                      }}
                    />
                    {userType === 'professional' && (
                      <button
                        className="text-[#464452] dark:text-white/70 hover:text-[#936FEF] transition-colors"
                        title={t('social.messages.sendAppointmentLink')}
                      >
                        <Calendar className="w-6 h-6" />
                      </button>
                    )}
                    <button
                      onClick={() => {
                        void handleSendMessage();
                      }}
                      disabled={!messageText.trim() || sending}
                      className="p-2 rounded-full text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        background:
                          'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                      }}
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ) : !isMobile ? (
              <div className="flex-1 flex items-center justify-center min-w-0">
                <div className="text-center p-4">
                  <MessageCircle className="w-16 h-16 mx-auto mb-4 text-[#464452] dark:text-white/20 opacity-20" />
                  <p
                    className="text-[#464452] dark:text-white/70 break-words"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('social.messages.selectConversation')}
                  </p>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
